import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowLeft, Loader2, CheckCircle, CreditCard, Tag, Globe, ShieldCheck, Sparkles,
} from "lucide-react";
import api, { formatApiError } from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import useTheme from "@/utils/useTheme";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

// Load Razorpay SDK once
const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

const CheckoutPage = () => {
  useTheme();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const urlCode = (params.get("code") || "").toUpperCase();
  const [qty, setQty] = useState(urlCode ? 1 : 10);
  const [coupon, setCoupon] = useState(urlCode);
  const [appliedCoupon, setAppliedCoupon] = useState(urlCode);
  const [tool, setTool] = useState(params.get("tool") === "nutrition" ? "nutrition" : "growth");
  const isPersonal = appliedCoupon.startsWith("T360P-");
  const qtyLocked = isPersonal || appliedCoupon === "TOTS360-100FREE";
  const [currency, setCurrency] = useState(null); // null=auto
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const back = "/";
  const requireCredits = loc.state?.requireCredits || false;

  // Auto-detect currency from user country (server confirms)
  const initialCurrency = (user?.country || "").toLowerCase().includes("india") ? "INR" : null;

  const fetchQuote = useCallback(async () => {
    if (!qty || qty < 1) return;
    try {
      const { data } = await api.post(`${API}/payments/quote`, {
        qty: parseInt(qty),
        currency: currency || initialCurrency,
        coupon_code: appliedCoupon || null,
        tool: isPersonal ? tool : null,
      });
      setQuote(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [qty, currency, appliedCoupon, initialCurrency, isPersonal, tool]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const applyCoupon = () => {
    const code = (coupon || "").trim().toUpperCase();
    if (!code) { toast.error("Enter a coupon code"); return; }
    setAppliedCoupon(code);
    // TOTS360-100FREE grants exactly 1 free credit per redemption
    if (code.startsWith("T360P-")) {
      setQty(1);
      toast.info("Your personal code covers 1 report per tool.");
    }
    if (code === "TOTS360-100FREE") {
      setQty(1);
      toast.info("Free coupon applies to 1 credit only.");
    }
  };

  const removeCoupon = () => {
    setCoupon("");
    setAppliedCoupon("");
  };

  const fmt = (amt) =>
    quote?.currency === "INR"
      ? `₹${Number(amt).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : `$${Number(amt).toFixed(2)}`;

  const payWithRazorpay = async () => {
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Payment SDK failed to load. Please retry."); setPaying(false); return; }
      const { data } = await api.post(`${API}/payments/razorpay/order`, {
        qty: parseInt(qty),
        coupon_code: appliedCoupon || null,
        tool: isPersonal ? tool : null,
      });

      const options = {
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "tots360",
        description: `${qty} report credit${qty > 1 ? "s" : ""}`,
        prefill: data.user_prefill,
        theme: { color: "#4ECDC4" },
        handler: async (resp) => {
          try {
            const { data: verify } = await api.post(`${API}/payments/razorpay/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Payment successful! +${verify.credits_added} credits.`);
            nav(`/checkout/success?gateway=razorpay&credits=${verify.credits_added}&balance=${verify.balance}`);
          } catch (e) {
            toast.error(formatApiError(e));
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => { setPaying(false); toast("Payment cancelled."); },
        },
      };
      const rp = new window.Razorpay(options);
      rp.open();
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(false);
    }
  };

  const payWithStripe = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`${API}/payments/stripe/checkout`, {
        qty: parseInt(qty),
        coupon_code: appliedCoupon || null,
        tool: isPersonal ? tool : null,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPaying(false);
    }
  };

  const payFree = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`${API}/payments/free-redeem`, {
        qty: parseInt(qty),
        coupon_code: appliedCoupon,
        currency: quote?.currency || null,
      });
      toast.success(`Coupon redeemed! +${data.credits_added} free credits.`);
      nav(`/checkout/success?gateway=free&credits=${data.credits_added}&balance=${data.balance}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  const onPay = () => {
    if (!quote) { toast.error("Loading price..."); return; }
    if (quote.total <= 0) return payFree();
    if (quote.currency === "INR") payWithRazorpay();
    else payWithStripe();
  };

  return (
    <div className="min-h-screen bg-[#0A1029] text-white">
      <header className="border-b border-white/10 px-6 py-4 flex items-center gap-4">
        <button onClick={() => nav(back)} className="text-white/60 hover:text-white flex items-center gap-2"
          data-testid="checkout-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>
        <h1 className="text-lg font-display">Buy Report Credits</h1>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {requireCredits && (
          <div className="mb-6 rounded-xl border border-[#FFE66D]/30 bg-[#FFE66D]/[0.05] px-4 py-3 text-sm text-[#FFE66D]"
            data-testid="credits-required-banner">
            You need at least 1 report credit to start an analysis. Choose a quantity below to continue.
          </div>
        )}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h2 className="text-3xl md:text-4xl font-display tracking-tight">Pay-per-report — buy what you need</h2>
          <p className="text-white/50 mt-2">
            {isPersonal
              ? "Your Parent Quiz code buys one report for the tool you pick below. It never expires."
              : "Each credit unlocks one full PDF report (BoneAH or NIN-NutriGuide). Credits never expire."}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: Configure */}
          <Card className="lg:col-span-3 bg-white/[0.03] border-white/10">
            <CardContent className="p-6 space-y-6">
              <div>
                <Label className="text-white/70 mb-2 block">
                  Number of reports
                  {qtyLocked && (
                    <span className="ml-2 text-xs text-[#4ECDC4]">(locked to 1 with {isPersonal ? "your personal code" : "free coupon"})</span>
                  )}
                </Label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[1, 5, 10, 25, 50, 100].map((n) => (
                    <button key={n} type="button"
                      onClick={() => !qtyLocked && setQty(n)}
                      disabled={qtyLocked}
                      className={`px-4 py-2 rounded-full border text-sm transition-all
                        ${parseInt(qty) === n ? "border-[#4ECDC4] bg-[#4ECDC4]/10 text-white" : "border-white/15 text-white/60 hover:border-white/30"}
                        ${qtyLocked ? "opacity-40 cursor-not-allowed" : ""}`}
                      data-testid={`qty-preset-${n}`}>
                      {n}
                    </button>
                  ))}
                </div>
                <Input type="number" min={1} max={1000} value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  disabled={qtyLocked}
                  className="bg-[#0F1C3F] border-white/10 text-white"
                  data-testid="checkout-qty-input" />
                <p className="text-xs text-white/40 mt-1">Min 1 · Max 1,000</p>
              </div>

              {isPersonal && (
                <div data-testid="personal-code-tool-picker">
                  <Label className="text-white/70 mb-2 block">Which report is this for?</Label>
                  <div className="flex gap-2">
                    {[["growth", "Growth Assessment"], ["nutrition", "NIN-NutriGuide"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setTool(v)} data-testid={`personal-code-tool-${v}`}
                        className={`flex-1 px-4 py-2 rounded-lg border text-sm transition-all ${tool === v ? "border-[#FFE66D] bg-[#FFE66D]/10" : "border-white/10 hover:border-white/30"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-white/40 mt-1">Parent Quiz price: ₹299 + GST or $10, once per tool. This credit works only for the tool you pick.</p>
                </div>
              )}

              <div>
                <Label className="text-white/70 mb-2 block">Currency</Label>
                <div className="flex gap-2">
                  {[
                    { v: null, l: "Auto-detect", hint: quote?.auto_currency ? `(${quote.auto_currency})` : "" },
                    { v: "INR", l: "₹ INR (Razorpay)" },
                    { v: "USD", l: "$ USD (Stripe)" },
                  ].map((opt) => (
                    <button key={String(opt.v)} type="button" onClick={() => setCurrency(opt.v)}
                      className={`flex-1 px-4 py-2 rounded-lg border text-sm transition-all
                        ${currency === opt.v ? "border-[#4ECDC4] bg-[#4ECDC4]/10" : "border-white/10 hover:border-white/30"}`}
                      data-testid={`currency-${opt.v || "auto"}`}>
                      {opt.l} <span className="text-white/40 text-xs">{opt.hint || ""}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-white/70 mb-2 block flex items-center gap-2">
                  <Tag className="w-3.5 h-3.5" /> Have a coupon?
                </Label>
                {!appliedCoupon ? (
                  <div className="flex gap-2">
                    <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                      placeholder="Enter coupon code" className="bg-[#0F1C3F] border-white/10 text-white uppercase"
                      data-testid="coupon-input" />
                    <Button onClick={applyCoupon} variant="outline"
                      className="border-[#4ECDC4]/40 text-[#4ECDC4] hover:bg-[#4ECDC4]/10"
                      data-testid="coupon-apply-btn">Apply</Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-[#4ECDC4]/[0.08] border border-[#4ECDC4]/30 rounded-lg px-3 py-2"
                    data-testid="coupon-applied-banner">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#4ECDC4]" />
                      <span className="text-sm">Coupon <strong className="text-[#4ECDC4]">{appliedCoupon}</strong> applied</span>
                    </div>
                    <button onClick={removeCoupon} className="text-xs text-white/50 hover:text-white"
                      data-testid="coupon-remove-btn">Remove</button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right: Summary */}
          <Card className="lg:col-span-2 bg-white/[0.03] border-white/10 h-fit sticky top-6">
            <CardContent className="p-6">
              <h3 className="font-display text-lg mb-4">Order summary</h3>
              {!quote ? (
                <div className="py-12 text-center text-white/40">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : (
                <div className="space-y-3 text-sm" data-testid="order-summary">
                  <Row label={`${quote.qty} × ${fmt(quote.unit_price)}`} value={fmt(quote.subtotal)} />
                  {quote.discount > 0 && (
                    <Row label={isPersonal ? `Parent Quiz code (${tool === "growth" ? "Growth" : "NutriGuide"})` : `Coupon ${quote.coupon_code}`} value={`- ${fmt(quote.discount)}`} valueClass="text-[#4ECDC4]" />
                  )}
                  {quote.gst_amount > 0 && (
                    <Row label="GST (18%)" value={fmt(quote.gst_amount)} valueClass="text-white/70" />
                  )}
                  <div className="border-t border-white/10 my-2" />
                  <Row label="Total" value={fmt(quote.total)} bold large />
                  <Button onClick={onPay} disabled={paying}
                    className="w-full mt-4 bg-[#4ECDC4] hover:bg-[#3db8b0] text-[#0A1029] font-semibold"
                    data-testid="checkout-pay-btn">
                    {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (
                      quote.total <= 0 ? <Sparkles className="w-4 h-4 mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    {quote.total <= 0 ? `Redeem ${qty} Free Credit${qty > 1 ? "s" : ""}` : `Pay ${fmt(quote.total)}`}
                  </Button>
                  <div className="flex items-center justify-center gap-4 text-[11px] text-white/40 mt-3">
                    <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Secure</span>
                    <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {quote.currency === "INR" ? "Razorpay" : "Stripe"}</span>
                    <span className="flex items-center gap-1"><Sparkles className="w-3 h-3" /> Instant</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-xs text-white/30 mt-8">
          By completing payment you agree to our{" "}
          <Link to="/terms" className="underline hover:text-white" data-testid="checkout-terms-link">Terms &amp; Conditions</Link>
          {", "}
          <Link to="/refund-policy" className="underline hover:text-white" data-testid="checkout-refund-link">Refund Policy</Link>{" "}
          and{" "}
          <Link to="/privacy-policy" className="underline hover:text-white" data-testid="checkout-privacy-link">Privacy Policy</Link>.
        </p>
      </main>
      <Toaster position="top-right" />
    </div>
  );
};

const Row = ({ label, value, bold, large, valueClass }) => (
  <div className="flex items-center justify-between">
    <span className={`${bold ? "font-semibold text-white" : "text-white/60"}`}>{label}</span>
    <span className={`${bold ? "font-bold" : ""} ${large ? "text-xl" : ""} ${valueClass || "text-white"}`}>{value}</span>
  </div>
);

export default CheckoutPage;
